import { useEffect } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import { PHONE } from "@/mocks/updiag";

const departments = [
  { code: "75", name: "Paris", slug: "paris-75", cities: "20 arrondissements", diags: "+1 800 diagnostics", highlight: true },
  { code: "92", name: "Hauts-de-Seine", slug: "hauts-de-seine-92", cities: "Boulogne, Neuilly, Levallois...", diags: "+650 diagnostics" },
  { code: "94", name: "Val-de-Marne", slug: "val-de-marne-94", cities: "Créteil, Vincennes, Vitry...", diags: "+420 diagnostics" },
  { code: "93", name: "Seine-Saint-Denis", slug: "seine-saint-denis-93", cities: "Saint-Denis, Montreuil, Aubervilliers...", diags: "+380 diagnostics" },
  { code: "91", name: "Essonne", slug: "essonne-91", cities: "Évry, Corbeil, Massy, Palaiseau...", diags: "+290 diagnostics" },
  { code: "78", name: "Yvelines", slug: "yvelines-78", cities: "Versailles, Saint-Germain, Mantes...", diags: "+310 diagnostics" },
  { code: "95", name: "Val-d'Oise", slug: "val-d-oise-95", cities: "Cergy, Argenteuil, Pontoise...", diags: "+260 diagnostics" },
  { code: "77", name: "Seine-et-Marne", slug: "seine-et-marne-77", cities: "Melun, Meaux, Chelles, Fontainebleau...", diags: "+240 diagnostics" },
];

export default function ZonesPage() {
  useEffect(() => {
    document.title = "Zones d'intervention IDF — Diagnostic Immobilier Paris & Île-de-France | Up Diag";
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "Inter, sans-serif" }}>
      <Header />

      {/* Hero */}
      <section className="relative py-20 bg-[#0F3B5F] overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img src="/img/readdy/zones-hero.jpg" alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#F97316]/20 border border-[#F97316]/40 rounded-full text-[#F97316] text-sm font-semibold mb-6">
            <i className="ri-map-2-line"></i>
            Île-de-France — 8 départements couverts
          </div>
          <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-4" style={{ fontFamily: "Barlow, sans-serif" }}>
            Zones d'intervention
          </h1>
          <p className="text-white/80 text-lg mb-8">
            Up Diag intervient dans toute l'Île-de-France — Paris et les 7 départements de la grande couronne. Délai d'intervention sous 48h garanti.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={`tel:${PHONE.replace(/\s/g, "")}`} className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-white text-[#0F3B5F] rounded-xl font-bold hover:bg-gray-100 transition-all whitespace-nowrap">
              <i className="ri-phone-fill"></i>{PHONE}
            </a>
            <Link to="/devis" className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-[#F97316] text-white rounded-xl font-bold hover:bg-orange-600 transition-all whitespace-nowrap">
              <i className="ri-file-list-3-line"></i>Devis gratuit
            </Link>
          </div>
        </div>
      </section>

      {/* Départements grid */}
      <section className="py-16 max-w-7xl mx-auto px-6">
        <div className="text-center mb-10">
          <h2 className="text-3xl font-extrabold text-[#0F3B5F] mb-3" style={{ fontFamily: "Barlow, sans-serif" }}>
            Choisissez votre département
          </h2>
          <p className="text-gray-500">Cliquez sur votre département pour voir les villes couvertes, les tarifs et le formulaire de devis</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {departments.map((dept) => (
            <Link
              key={dept.slug}
              to={`/zones/${dept.slug}`}
              className={`group relative rounded-2xl p-6 border transition-all duration-200 cursor-pointer hover:-translate-y-1 ${
                dept.highlight
                  ? "bg-[#0F3B5F] border-[#0F3B5F] text-white"
                  : "bg-white border-gray-100 hover:border-[#F97316] text-[#334155]"
              }`}
            >
              <div className={`w-12 h-12 flex items-center justify-center rounded-xl text-xl font-extrabold mb-4 ${dept.highlight ? "bg-[#F97316] text-white" : "bg-[#0F3B5F]/10 text-[#0F3B5F]"}`} style={{ fontFamily: "Barlow, sans-serif" }}>
                {dept.code}
              </div>
              <h3 className={`text-lg font-extrabold mb-1 ${dept.highlight ? "text-white" : "text-[#0F3B5F]"}`} style={{ fontFamily: "Barlow, sans-serif" }}>
                {dept.name}
              </h3>
              <p className={`text-xs mb-3 leading-relaxed ${dept.highlight ? "text-white/70" : "text-gray-400"}`}>{dept.cities}</p>
              <div className={`text-xs font-semibold ${dept.highlight ? "text-[#F97316]" : "text-[#F97316]"}`}>{dept.diags}</div>
              <div className={`absolute top-5 right-5 w-7 h-7 flex items-center justify-center rounded-full transition-all ${dept.highlight ? "bg-white/20" : "bg-gray-100 group-hover:bg-[#F97316] group-hover:text-white"}`}>
                <i className="ri-arrow-right-line text-sm"></i>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Garanties */}
      <section className="py-14 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: "ri-shield-check-line", title: "Certifié COFRAC", desc: "Tous nos diagnostiqueurs sont certifiés et accrédités COFRAC" },
              { icon: "ri-time-line", title: "Sous 48h", desc: "Intervention garantie sous 48h dans toute l'Île-de-France" },
              { icon: "ri-file-check-line", title: "Rapport le jour même", desc: "Vos rapports certifiés envoyés par email le jour de la visite" },
              { icon: "ri-money-euro-circle-line", title: "Devis gratuit", desc: "Devis personnalisé gratuit en 2 minutes, sans engagement" },
            ].map((g) => (
              <div key={g.title} className="flex gap-4 p-5 bg-white rounded-xl border border-gray-100">
                <div className="w-10 h-10 flex items-center justify-center bg-[#0F3B5F]/10 rounded-lg flex-shrink-0">
                  <i className={`${g.icon} text-[#0F3B5F] text-lg`}></i>
                </div>
                <div>
                  <div className="font-semibold text-[#0F3B5F] text-sm mb-1">{g.title}</div>
                  <div className="text-gray-500 text-xs leading-relaxed">{g.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
